'use client';

import React, { createContext, useContext, useEffect, useId, useState } from 'react';
import { mergeClassNames } from '../../../_utils/mergeClassNames';
import './sticky-input-column.scss';

const DESKTOP_MEDIA_QUERY = '(min-width: 768px)';

type CollapsibleCardContextValue = {
  bodyId: string;
  collapsible: boolean;
  expanded: boolean;
  headerId: string;
  toggle: () => void;
};

const CollapsibleCardContext = createContext<CollapsibleCardContextValue | null>(null);

function useCollapsibleCard() {
  return useContext(CollapsibleCardContext);
}

function useResponsiveDefaultExpanded(collapsible: boolean) {
  const [expanded, setExpanded] = useState(false);
  const [hasUserToggled, setHasUserToggled] = useState(false);

  useEffect(() => {
    if (!collapsible || hasUserToggled) {
      return;
    }

    const mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
    const syncExpanded = () => {
      setExpanded(mediaQuery.matches);
    };

    syncExpanded();
    mediaQuery.addEventListener('change', syncExpanded);
    return () => mediaQuery.removeEventListener('change', syncExpanded);
  }, [collapsible, hasUserToggled]);

  const toggle = () => {
    setHasUserToggled(true);
    setExpanded((current) => !current);
  };

  return { expanded, toggle };
}

export function Shimmer({ className }: { className?: string }) {
  return <div className={`animate-pulse rounded-md bg-bzblue-400/35 ${className ?? ''}`} aria-hidden />;
}

export function CalculatorCard({
  children,
  className,
  collapsible = false,
}: {
  children: React.ReactNode;
  className?: string;
  collapsible?: boolean;
}) {
  const headerId = useId();
  const bodyId = useId();
  const { expanded, toggle } = useResponsiveDefaultExpanded(collapsible);

  return (
    <CollapsibleCardContext.Provider
      value={{
        bodyId,
        collapsible,
        expanded,
        headerId,
        toggle,
      }}
    >
      <div
        className={mergeClassNames(
          'overflow-hidden rounded-md border border-bzblue-400 bg-white shadow-sm',
          className,
        )}
      >
        {children}
      </div>
    </CollapsibleCardContext.Provider>
  );
}

export function CalculatorStickyInputColumn({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={mergeClassNames('sticky-input-column min-w-0 space-y-5 lg:col-span-2', className)}
    >
      {children}
    </div>
  );
}

function CollapsibleChevron({ expanded }: { expanded: boolean }) {
  return (
    <svg
      aria-hidden
      className={mergeClassNames(
        'h-4 w-4 shrink-0 text-bzblue-900 transition-transform duration-200',
        expanded ? 'rotate-180' : 'rotate-0',
      )}
      fill="none"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
    </svg>
  );
}

export function CalculatorCardHeader({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const collapsibleCard = useCollapsibleCard();
  const isCollapsible = collapsibleCard?.collapsible ?? false;
  const expanded = collapsibleCard?.expanded ?? true;

  if (!isCollapsible || !collapsibleCard) {
    return (
      <div
        className={mergeClassNames('border-b border-bzblue-400 bg-bzblue-100 px-4 py-3 md:px-5', className)}
      >
        {children}
      </div>
    );
  }

  return (
    <button
      aria-controls={collapsibleCard.bodyId}
      aria-expanded={expanded}
      className={mergeClassNames(
        'flex w-full items-center justify-between gap-3 border-b border-bzblue-400 bg-bzblue-100 px-4 py-3 text-left md:px-5',
        className,
      )}
      id={collapsibleCard.headerId}
      onClick={collapsibleCard.toggle}
      type="button"
    >
      <div className="min-w-0 flex-1">{children}</div>
      <CollapsibleChevron expanded={expanded} />
    </button>
  );
}

export function CalculatorCardTitle({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <p
      className={mergeClassNames(
        'text-xs font-semibold uppercase tracking-wide text-bzblue-900',
        className,
      )}
    >
      {children}
    </p>
  );
}

export function CalculatorCardBody({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const collapsibleCard = useCollapsibleCard();
  const isCollapsible = collapsibleCard?.collapsible ?? false;
  const expanded = collapsibleCard?.expanded ?? true;

  return (
    <div
      aria-labelledby={isCollapsible ? collapsibleCard?.headerId : undefined}
      className={mergeClassNames('px-4 py-4 md:px-5 md:py-5', !expanded && isCollapsible && 'hidden', className)}
      hidden={isCollapsible ? !expanded : undefined}
      id={isCollapsible ? collapsibleCard?.bodyId : undefined}
    >
      {children}
    </div>
  );
}

const CALCULATOR_TABLE_HEAD_ROW_CLASS = 'bg-[#f2f8ff]';

export const calculatorTableSelectedRowClass =
  'border-l-[3px] border-l-[#3f83f8] bg-[rgba(63,131,248,0.05)]';

export const calculatorTableUnselectedRowClass =
  'border-l-[3px] border-l-transparent hover:bg-[#f8fafc]';

export function CalculatorTableHeadRow({
  children,
  className,
  underInset = false,
  ...props
}: React.ComponentPropsWithoutRef<'tr'> & { underInset?: boolean }) {
  return (
    <tr
      className={mergeClassNames(
        CALCULATOR_TABLE_HEAD_ROW_CLASS,
        underInset ? undefined : 'border-b border-bzblue-400',
        className,
      )}
      {...props}
    >
      {children}
    </tr>
  );
}

export function CalculatorTableHeadCell({
  children,
  className,
  ...props
}: React.ComponentPropsWithoutRef<'th'>) {
  return (
    <th
      className={mergeClassNames(
        'px-4 py-3 md:px-5 text-xs font-semibold uppercase tracking-wide text-bzblue-900',
        className,
      )}
      {...props}
    >
      {children}
    </th>
  );
}

export function CalculatorTableBodyRow({
  children,
  className,
  ...props
}: React.ComponentPropsWithoutRef<'tr'>) {
  return (
    <tr
      className={mergeClassNames('border-b border-bzblue-400 last:border-b-0', className)}
      {...props}
    >
      {children}
    </tr>
  );
}

export function CalculatorTableBodyCell({
  children,
  className,
  ...props
}: React.ComponentPropsWithoutRef<'td'>) {
  return (
    <td className={mergeClassNames('px-4 py-3 align-middle md:px-5', className)} {...props}>
      {children}
    </td>
  );
}
